<template>
  <div class="e-upload">
    <div class="e-upload__drop-area">
      <div
        ref="dropArea"
        @dragover.prevent
        @dragenter="$refs.dropArea.classList.add('on-drag')"
        @dragleave="$refs.dropArea.classList.remove('on-drag')"
        class="e-upload__drop-area-box"
        :class="{ '--disabled' : disabled}"
        @drop.stop.prevent="handleDrop"
        @click="triggerFileInput"
      >
        <p v-html="disabled ? $t('system.uploadBox.dropFileTextDisabled') : $t('system.uploadBox.dropFileText')"></p>
        <input
          type="file"
          ref="fileInput"
          class="hidden-input"
          multiple
          @change="handleSelect"
        />
      </div>
    </div>
    <div
      v-if="attachmentCount > 0"
      class="e-upload__attachment-box">
      <div class="e-upload__attachments">
        <div 
          v-for="(attachment, index) in combinedList"
          :key="index"
          class="e-upload__attachment"
        >
          <a v-if="attachment.name.split('.').pop() === 'pdf'" class="pdf-container" target="_blank" :href="attachment.url">
            <IconPDF class="pdf-icon" />
            <div class="pdf-name">
              <div class="pdf-filename">{{ attachment.name }}</div>
            </div>
          </a>
          <div v-else class="image-container">
            <a target="_blank" :href="attachment.url">
              <img
                v-if="attachment.type === 'existing'"
                draggable="false"
                :src="attachment.preview"
                alt="preview"
                loading="lazy"
              />
            </a>
          </div>
          <Loading v-if="attachment.type === 'uploading'"/>
          <button class="e-upload__action--remove-attachment" v-if="attachment.type === 'existing'"
            @click="handleRemove(attachment)">
            <Trash />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
  
<script>
import { mapGetters } from 'vuex'
import { restapi } from 'api/config'
import IconPDF from 'assets/icons/pdf_icon.svg'
import Loading from 'atoms/Loading/Loading'
import Trash from 'assets/icons/trash.svg'
export default {
  name: 'UploadBox',
  components: {
    IconPDF,
    Loading,
    Trash
  },
  props: {
    acceptedFiles: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: true
    },
    existingAttachments: {
      type: Array,
      default: () => []
    },
    maxFiles: {
      type: Number,
      default: 5
    },
    maxFileSize: {
      type: Number,
      default: 10
    },
    messageId: {
      type: String,
      default: ""
    },
    rootId: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      newFiles: [],
      uploadingAttachments: []
    }
  },
  computed: {
    ...mapGetters([
      'userInfo'
    ]),
    combinedList() {
      const existing = this.existingAttachments.map((f) => ({
        name: f.filename,
        preview: this.downloadLink(f.id),
        url: this.attachmentLink(f.id),
        type: 'existing',
        id: f.id
      }));
      const uploading = this.uploadingAttachments.map((f) => ({
        name: f.name,
        preview: f.preview,
        url: '',
        type: 'uploading',
        id: f.id
      }));

      return [...existing, ...uploading];
    },
    attachmentCount() {
      if(this.combinedList) {
        return this.combinedList.length
      }
      return 0
    }
  },
  methods: {
    triggerFileInput() {
      if(this.disabled) {
        return
      }
      this.$refs.fileInput.click();
    },
    handleSelect(event) {
      if(this.disabled) {
        return
      }
      this.processFiles(event.target.files);
    },
    handleDrop(event) {
      if(this.disabled) {
        return
      }
      this.processFiles(event.dataTransfer.files);
    },
    async processFiles(fileList) {
      if(fileList.length + this.attachmentCount > this.maxFiles) {
        this.$message({
          type: 'warning',
          message: this.$t('system.uploadBox.fileLimitExceeded')
        })
        return
      }

      for (const file of fileList) {
        if(this.validateFile(file)) { 
          const attachment = {
            file: file,
            id: crypto.randomUUID()
          }
          this.uploadingAttachments.push({
            name: file.name,
            id: attachment.id,
            preview: URL.createObjectURL(file)
          })
          await this.$emit('onUpload', attachment)
        }
      }
    },
    removeTempAttachment(tempId) {
      this.uploadingAttachments = this.uploadingAttachments.filter((attachment) => attachment.id != tempId)
    },
    validateFile(file) {
      let isAccepted = this.acceptedFiles.includes(file.type)
      if(!isAccepted) {
        this.$message({
          type: 'warning',
          message: this.$t('system.uploadBox.fileTypeNotSupported')
        })
        return isAccepted
      }
      isAccepted = file.size / (1024 * 1024) <= this.maxFileSize
      if(!isAccepted) {
        this.$message({
          type: 'warning',
          message: this.$t('system.uploadBox.fileSizeToLarge')
        })
      }
      return isAccepted
    },
    handleRemove(file) {
      this.$emit('onAttachmentRemoved', file)
    },
    attachmentLink(attachmentId) {
      if (attachmentId) {
        if (restapi[process.env.NODE_ENV]) {
          return `${restapi[process.env.NODE_ENV].url}/users/${this.userInfo.id}/dialogs/${this.rootId}/messages/${this.messageId}/attachments/${attachmentId}/view`
        }
      }
    },
    downloadLink(attachmentId) {
      if(attachmentId) {
        if(restapi[process.env.NODE_ENV]){
          return `${restapi[process.env.NODE_ENV].url}/users/${this.userInfo.id}/dialogs/${this.rootId}/messages/${this.messageId}/attachments/${attachmentId}/download`
        }
      }
      return ""
    },
  }
}
</script>
  
<style lang="scss">
  @import '_assets/UploadBox';
</style>